/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import ReactMarkdown from 'react-markdown';

import Imgix from 'js/components/Imgix';
import { isRightToLeft } from 'js/lib/language';

import { AlwaysLightMode, Button, Grid, Hidden, IconButton, Link, Typography2, breakpoints } from '@coursera/cds-core';
import type { MerchandisingModule } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import type { ModuleLayoutConfig } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import ModuleLayout, { noImage } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { ImageType } from 'bundles/cds-labs/components/Modules/components/Image';
import Image from 'bundles/cds-labs/components/Modules/components/Image';
import {
  TYPOGRAPHY_DARK_COLOR,
  TYPOGRAPHY_LIGHT_COLOR,
  getColorFromColorName,
  getCustomBackgroundStyle,
  parseTextColorOverride,
  resolveIsDarkBackground,
} from 'bundles/cds-labs/components/Modules/utils/colors';
import type { TextColorOverride } from 'bundles/cds-labs/components/Modules/utils/colors';

import promoBarLeftImage from 'images/cds-labs/promo_bar_left.png';
import promoBarRightImage from 'images/cds-labs/promo_bar_right.png';

import _t from 'i18n!nls/cds-labs';

const styles = {
  bannerContainerWithImages: css`
    border-radius: var(--cds-border-radius-200);
    overflow: hidden;
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
  `,
  bannerContainerWithoutImages: css`
    border-radius: var(--cds-border-radius-200);
    overflow: hidden;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    ${breakpoints.down('sm')} {
      align-items: flex-start;
    }
  `,
  content: css`
    flex: 1;

    && {
      padding-left: var(--cds-spacing-200);
      padding-right: var(--cds-spacing-200);
    }
  `,
  leftContent: (showBannerLogo: boolean) => css`
    padding: var(--cds-spacing-150) var(--cds-spacing-200);
    display: flex;
    flex-direction: ${showBannerLogo ? 'row' : 'column'};

    ${breakpoints.down('xs')} {
      flex-direction: column;
    }
  `,
  centeredContent: css`
    text-align: center;
    padding: var(--cds-spacing-150) 0;
    display: flex;
    flex-direction: column;
  `,
  contentContainer: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
  `,
  logoContainer: css`
    display: flex;
    align-items: center;
    margin-right: var(--cds-spacing-400);
    min-width: 120px;
    ${breakpoints.down('xs')} {
      display: unset;
      margin-right: 0;
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  leftImageContainer: css`
    position: relative;
  `,
  leftImage: css`
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    min-width: 100%;
    min-height: 100%;
    height: 100%;
  `,
  rightImageContainer: css`
    display: flex;
    position: relative;
    justify-content: flex-end;
    padding-right: var(--cds-spacing-200);
    align-items: center;
    gap: var(--cds-spacing-100);

    ${breakpoints.down('sm')} {
      align-items: start;
      padding-top: var(--cds-spacing-200);
    }
  `,
  rtlImage: css`
    transform: scaleX(-1);
  `,
  rightImage: css`
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    min-width: 100%;
    min-height: 100%;
    height: 100%;
  `,
  bannerTextContainerSingleLine: css`
    display: block;
  `,
  bannerTextBodySingleLine: css`
    display: inline;
  `,
  buttonTray: css`
    display: flex;
    flex-direction: row;
    gap: var(--cds-spacing-100);
  `,
  leftContentButtonTray: css`
    display: inline-flex;
    justify-content: start;
    margin-top: var(--cds-spacing-200);
  `,
  centeredContentButtonTray: css`
    display: inline-flex;
    justify-content: center;
    margin-top: var(--cds-spacing-200);
  `,
  headline: css`
    margin-bottom: 2px;
  `,
  markdown: css`
    > *:last-child {
      margin: 0;
    }
  `,
  markdownSingleLine: css`
    display: inline;

    * {
      display: inline;
    }
  `,
  actions: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--cds-spacing-100);
    padding-right: var(--cds-spacing-200);

    ${breakpoints.down('sm')} {
      padding-top: var(--cds-spacing-50);
    }
  `,
  invertedTextColor: css`
    color: var(--cds-color-neutral-background-primary);

    &:hover,
    &:focus,
    &:focus-visible,
    &:visited,
    &:visited:not(.cds-link-focusVisible) {
      a& {
        color: var(--cds-color-neutral-background-primary);
      }
    }
  `,
};

const TEXT_LAYOUT_OPTIONS = {
  'separate-lines': 'separate-lines',
  'single-line': 'single-line',
} as const;

const BANNER_COLORS = {
  'light-blue': 'light-blue',
  'light-purple': 'light-purple',
  'misty-blue': 'misty-blue',
  white: 'white',
  black: 'black',
  blue: 'blue',
  grey: 'grey',
  'dark-blue': 'dark-blue',
  'strong-blue': 'strong-blue',
  'strong-grey': 'strong-grey',
  'neutral-grey': 'neutral-grey',
  'experimental-color-blue-abstract-gradient': 'experimental-color-blue-abstract-gradient',
  'experimental-color-pink-abstract-gradient': 'experimental-color-pink-abstract-gradient',
  'experimental-color-expressive-yellow': 'experimental-color-expressive-yellow',
  'primary-blue-xxstrong': 'primary-blue-xxstrong',
} as const;

type TextLayoutOptions = keyof typeof TEXT_LAYOUT_OPTIONS;

type BannerColors = keyof typeof BANNER_COLORS;

type Cta = {
  text: string;
  href: string;
  onClick?: () => void;
  openInNewTab?: boolean;
};

type Props = {
  headline: string;
  description: string;
  textLayout: TextLayoutOptions;
  centerContent?: boolean;
  bannerColor: BannerColors;
  isDismissed?: boolean;
  onDismiss: () => void;
  cta?: Cta;
  hideBackgroundImages?: boolean | null;
  moduleEventingData?: MerchandisingModule;
  bannerLogo?: ImageType | null;
  customBackgroundColor?: string | null;
  textColorOverride?: TextColorOverride;
} & Pick<ModuleLayoutConfig, 'layout'>;

function PromoBarModule(props: Props) {
  const {
    headline,
    description,
    centerContent: centerContentProp,
    textLayout,
    isDismissed,
    bannerColor = 'light-blue',
    onDismiss,
    cta,
    hideBackgroundImages,
    moduleEventingData,
    bannerLogo,
    customBackgroundColor,
    textColorOverride,
  } = props;

  // If bannerLogo is present, force centerContent to false, for layout consistency
  const centerContent = bannerLogo ? false : centerContentProp;

  const isRtl = isRightToLeft(_t.getLocale());
  const parsedTextColorOverride = parseTextColorOverride(textColorOverride);
  const isDarkBackground = resolveIsDarkBackground({
    backgroundColor: bannerColor,
    customBackgroundColor,
    textColorOverride: parsedTextColorOverride,
  });

  const track = useTracker();
  const merchModuleForEventing: MerchandisingModule = {
    moduleType: 'promo_bar',
    heading: headline,
    // Guard against a nullable Contentful value reaching the event: the eventing schema
    // allows `body` to be absent but rejects an explicit null (expects String, found Null).
    body: description ?? undefined,
    ...moduleEventingData,
  };
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_merchandising_module',
    {
      merchandisingModule: merchModuleForEventing,
    }
  );

  const handleDismiss = () => {
    track('click_merchandising_module', {
      merchandisingModule: { moduleType: 'promo_bar', ...moduleEventingData },
      button: {
        name: 'dismiss',
      },
    });

    onDismiss();
  };

  const handleCtaClick = !cta
    ? undefined
    : () => {
        track('click_merchandising_module', {
          merchandisingModule: { moduleType: 'promo_bar', ...moduleEventingData },
          button: {
            name: 'promo_bar_cta',
            linkURL: cta.href,
            linkType: 'internal',
          },
        });

        if (cta.onClick) {
          cta.onClick();
        }
      };

  if (isDismissed) {
    return null;
  }

  const backgroundColorStyles = {
    backgroundColor: customBackgroundColor ? undefined : (getColorFromColorName(bannerColor ?? '') ?? undefined),
  };

  const customRenderers = {
    link: ({ href, children }: React.LinkHTMLAttributes<HTMLAnchorElement>) => {
      const handleMdClick = () => {
        track('click_merchandising_module', {
          merchandisingModule: { moduleType: 'promo_bar', ...moduleEventingData },
          button: {
            name: 'promo_bar_md',
            linkURL: href,
            linkType: 'internal',
          },
        });
      };

      return (
        <Link
          css={isDarkBackground ? styles.invertedTextColor : undefined}
          onClick={handleMdClick}
          href={href}
          variant="standard"
          typographyVariant="inherit"
          iconPosition="after"
        >
          {children}
        </Link>
      );
    },
  };

  const ctaButton = () => {
    if (!cta) return null;

    return (
      <Button
        size="small"
        variant="secondary"
        href={cta.href}
        component="a"
        onClick={handleCtaClick}
        target={cta.openInNewTab ? '_blank' : undefined}
        rel={cta.openInNewTab ? 'noopener noreferrer' : undefined}
      >
        {cta.text}
      </Button>
    );
  };

  const bannerText = () => {
    const isSingleLine = textLayout === 'single-line';

    return (
      <div css={isSingleLine ? styles.bannerTextContainerSingleLine : undefined}>
        {headline && (
          <Typography2
            component="h2"
            variant="subtitleMedium"
            css={[
              isSingleLine ? styles.bannerTextBodySingleLine : styles.headline,
              isDarkBackground ? styles.invertedTextColor : undefined,
            ]}
          >
            <ReactMarkdown source={headline} css={[styles.markdown, isSingleLine ? styles.markdownSingleLine : null]} />
          </Typography2>
        )}
        {isSingleLine && ' '}
        {description && (
          <Typography2
            component="div"
            variant="bodyPrimary"
            css={[
              isSingleLine ? styles.bannerTextBodySingleLine : undefined,
              isDarkBackground ? styles.invertedTextColor : undefined,
            ]}
          >
            <ReactMarkdown
              renderers={{ ...customRenderers }}
              source={description}
              css={[styles.markdown, isSingleLine ? styles.markdownSingleLine : null]}
            />
          </Typography2>
        )}
      </div>
    );
  };

  const dismissButton = (invert: boolean) => {
    return (
      <IconButton intent="close" variant={`ghost${invert ? 'Invert' : ''}`} onClick={handleDismiss} size="small" />
    );
  };

  const promoBarLogo = () =>
    bannerLogo ? (
      <div css={styles.logoContainer}>
        <Image {...bannerLogo} width={120} />
      </div>
    ) : null;

  const contentPanel = () => {
    if (hideBackgroundImages) {
      return (
        <div css={[styles.bannerContainerWithoutImages, backgroundColorStyles]} ref={visibilityTrackingRef}>
          <div css={[styles.content, centerContent ? styles.centeredContent : styles.leftContent(!!bannerLogo)]}>
            {promoBarLogo()}
            <div css={styles.contentContainer}>
              {bannerText()}
              {cta && (
                <Hidden mdUp>
                  <div
                    css={[
                      styles.buttonTray,
                      centerContent ? styles.centeredContentButtonTray : styles.leftContentButtonTray,
                    ]}
                  >
                    {ctaButton()}
                  </div>
                </Hidden>
              )}
            </div>
          </div>
          <div css={styles.actions}>
            <Hidden smDown>
              <div>{ctaButton()}</div>
            </Hidden>
            {dismissButton(isDarkBackground)}
          </div>
        </div>
      );
    }

    if (centerContent) {
      return (
        <Grid
          container
          item
          xs={12}
          css={[styles.bannerContainerWithImages, backgroundColorStyles]}
          spacing={0}
          ref={visibilityTrackingRef}
        >
          <Grid item xs={2} sm={3} css={styles.leftImageContainer}>
            <Imgix css={[styles.leftImage, isRtl ? styles.rtlImage : null]} src={promoBarLeftImage} alt="" />
          </Grid>
          <Grid item xs={8} sm={6} css={styles.centeredContent}>
            {bannerText()}
            {cta && (
              <Hidden mdUp>
                <div css={[styles.buttonTray, styles.centeredContentButtonTray]}>{ctaButton()}</div>
              </Hidden>
            )}
          </Grid>

          <Grid item xs={2} sm={3} css={styles.rightImageContainer}>
            <Imgix css={[styles.rightImage, isRtl ? styles.rtlImage : null]} src={promoBarRightImage} alt="" />
            <Hidden smDown>
              <div css={styles.buttonTray}>{ctaButton()}</div>
            </Hidden>
            <Hidden smUp>{dismissButton(false)}</Hidden>
            <Hidden only={['xs']}>{dismissButton(true)}</Hidden>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid
        container
        item
        xs={12}
        css={[styles.bannerContainerWithImages, backgroundColorStyles]}
        spacing={0}
        ref={visibilityTrackingRef}
      >
        <Grid item xs={10} sm={9} css={styles.leftContent(!!bannerLogo)}>
          {promoBarLogo()}
          <div css={styles.contentContainer}>
            {bannerText()}
            {cta && (
              <Hidden mdUp>
                <div css={[styles.buttonTray, styles.leftContentButtonTray]}>{ctaButton()}</div>
              </Hidden>
            )}
          </div>
        </Grid>
        <Grid item xs={2} sm={3} css={styles.rightImageContainer}>
          <Imgix css={[styles.rightImage, isRtl ? styles.rtlImage : null]} src={promoBarRightImage} alt="" />
          <Hidden smDown>
            <div css={styles.buttonTray}>{ctaButton()}</div>
          </Hidden>
          <Hidden smUp>{dismissButton(false)}</Hidden>
          <Hidden only={['xs']}>{dismissButton(true)}</Hidden>
        </Grid>
      </Grid>
    );
  };

  const modifiedAssembly = {
    ...noImage(props),
    removeTopPadding: true,
    removeBottomPadding: true,
    ...(customBackgroundColor ? { backgroundColor: undefined } : {}),
  };

  const moduleLayout = <ModuleLayout assembly={{ ...modifiedAssembly }} renderContent={contentPanel} />;

  if (customBackgroundColor) {
    const customBgParsed = getCustomBackgroundStyle(customBackgroundColor);
    const outerBgCss = customBgParsed
      ? css`
          ${customBgParsed.property}: ${customBgParsed.value};

          color: ${isDarkBackground ? TYPOGRAPHY_LIGHT_COLOR : TYPOGRAPHY_DARK_COLOR};
        `
      : undefined;

    return (
      <div css={outerBgCss}>
        <AlwaysLightMode>{moduleLayout}</AlwaysLightMode>
      </div>
    );
  }

  return moduleLayout;
}

export { PromoBarModule, TEXT_LAYOUT_OPTIONS, BANNER_COLORS };

export type { Props, TextLayoutOptions, BannerColors, Cta };

export default PromoBarModule;
